/**
 * PortfolioToolbarBar — extracted from PortfolioDashboard.tsx
 * (Phase c — Portfolio Off-Chain splitting, Pass 3).
 *
 * Composes the page-level "toolbar row" :
 *  - the OwlMascot perched at the top-right (clicks navigate to the
 *    crypto-quiz page, hover bubble shows a per-language CTA)
 *  - the existing `<PortfolioToolbar>` underneath, with all 17 props
 *    forwarded as-is.
 *
 * Why a thin pass-through component if it just forwards props?
 *  - Keeps `PortfolioDashboard.tsx`'s render under a manageable ceiling
 *    (the toolbar block was 42 LOC of inline JSX; now 1 line at the
 *    call site).
 *  - The owl is part of the toolbar's UX (it's positioned absolutely
 *    relative to it), not an independent decorative element. Bundling
 *    them together prevents accidental separation in future refactors.
 *
 * No state, no business logic. Every interaction is delegated via
 * callback props.
 */

import type { Coin, FilterConfig } from '../../types';
import type { UserMode } from '../../hooks/useUserMode';
import { OwlMascot } from '../OwlMascot';
import { PortfolioToolbar } from './PortfolioToolbar';

interface PortfolioToolbarBarProps {
  // OwlMascot wiring
  onOwlClick: () => void;
  owlTitle: string;
  owlMessage: string;

  // PortfolioToolbar pass-through — V2.G12.6: signature matches the
  // underlying PortfolioToolbar prop (coinId + symbol from the search).
  onAddToken: (coinId: string, symbol: string) => void | Promise<void>;
  coins: Coin[];
  tokensCount: number;
  filters: FilterConfig;
  onFiltersChange: (filters: FilterConfig) => void;
  onFiltersReset: () => void;
  onImportJSON: () => void;
  onImportCSV: () => void;
  onExportCSV: () => void;
  onExportJSON: () => void;
  onTaxExport: () => void;
  onLoadDemo: () => void;
  userMode: UserMode;
  showBTCComparison: boolean;
  onToggleBTCComparison: () => void;
  onSnapshot: () => void;
  onInvestmentSimulator: () => void;
}

export function PortfolioToolbarBar({
  onOwlClick,
  owlTitle,
  owlMessage,
  onAddToken,
  coins,
  tokensCount,
  filters,
  onFiltersChange,
  onFiltersReset,
  onImportJSON,
  onImportCSV,
  onExportCSV,
  onExportJSON,
  onTaxExport,
  onLoadDemo,
  userMode,
  showBTCComparison,
  onToggleBTCComparison,
  onSnapshot,
  onInvestmentSimulator,
}: PortfolioToolbarBarProps) {
  return (
    <div style={{ position: 'relative' }}>
      {/* Owl Mascot perched on toolbar */}
      <div
        onClick={onOwlClick}
        style={{
          position: 'absolute',
          top: '-25px',
          right: '20px',
          cursor: 'pointer',
          zIndex: 10,
        }}
        title={owlTitle}
      >
        <OwlMascot size="small" message={owlMessage} showBubbleOnHover />
      </div>

      <PortfolioToolbar
        onAddToken={onAddToken}
        coins={coins}
        tokensCount={tokensCount}
        filters={filters}
        onFiltersChange={onFiltersChange}
        onFiltersReset={onFiltersReset}
        onImportJSON={onImportJSON}
        onImportCSV={onImportCSV}
        onExportCSV={onExportCSV}
        onExportJSON={onExportJSON}
        onTaxExport={onTaxExport}
        onLoadDemo={onLoadDemo}
        userMode={userMode}
        showBTCComparison={showBTCComparison}
        onToggleBTCComparison={onToggleBTCComparison}
        onSnapshot={onSnapshot}
        onInvestmentSimulator={onInvestmentSimulator}
      />
    </div>
  );
}
